/* eslint-disable max-lines */
import { Card, Flex, Text } from '@mantine/core';
import dayjs from 'dayjs';
import 'dayjs/locale/id';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import Button from '../../components/elements/Button';
import LoadingBars from '../../components/elements/LoadingBars';
import Skeleton from '../../components/elements/Skeleton';
import { IconHistoryFilled } from '../../components/icons/IconHistoryFilled';
import { AppContext } from '../../contexts';
import { getCustomerTypeBadge } from '../../utils/customerTypeBadge';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { clearRedux as clearHistoryLogRedux, fetchRequestFile } from '../../components/elements/HistoryLog/actions';
import { fetchProvinceTransactionRestriction } from './action';
import DocumentPreviewModal from './DocumentPreviewModal';
import HistoryMonthYearFilter from './HistoryMonthYearFilter';
import HistoryPeriodNotice from './HistoryPeriodNotice';
import HistoryProvinceLog from './HistoryProvinceLog';
import styles from './detail.scoped.css';

function DetailDataRow({ label, value }) {
  return (
    <div className={styles.dataRow}>
      <div className={styles.dataLabel}>{label}</div>
      <div className={styles.dataValue}>
        <span className={styles.dataColon}>:</span>
        <div className={styles.dataValueContent}>{value}</div>
      </div>
    </div>
  );
}

DetailDataRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function DetailCustomerTypes({ customerTypes }) {
  return (
    <div className={styles.dataStack}>
      {customerTypes.map((type, index) => {
        const badge = getCustomerTypeBadge(type.sourceTypeId);

        return (
          <div
            className={styles.customerTypeRow}
            key={`${type.sourceTypeId || type.name}-${index}`}
          >
            <Text className={styles.customerTypeName}>{type.name}</Text>
            <div
              className={styles.customerTypeBadge}
              style={{
                backgroundColor: badge.backgroundColor,
                color: badge.textColor,
              }}
            >
              {badge.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

DetailCustomerTypes.propTypes = {
  customerTypes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      sourceTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
};

function DetailProvinceList({ provinces }) {
  return (
    <div className={styles.dataStack}>
      {provinces.map((province) => (
        <Text
          className={styles.provinceText}
          key={province.provinceId || province.provinceName}
        >
          {province.provinceName}
        </Text>
      ))}
    </div>
  );
}

DetailProvinceList.propTypes = {
  provinces: PropTypes.arrayOf(
    PropTypes.shape({
      provinceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      provinceName: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default function Detail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { setBreadcrumbs } = useContext(AppContext);
  const [isShowDataPlain, setIsShowDataPlain] = useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => dayjs().format('MM'));
  const [selectedYear, setSelectedYear] = useState(() => dayjs().format('YYYY'));
  const selectedYearMonth = `${selectedYear}-${selectedMonth}`;
  const { dataProvinceTransactionRestriction } = useSelector(
    (state) => state.provinceTransactionRestriction
  );
  const { dataRequestFile, isLoadingRequestFile } = useSelector(
    (state) => state.historyLog
  );
  const searchResult = dataProvinceTransactionRestriction?.data || {};
  const activeProvinces = searchResult?.provinces || [];
  const historyProvince = searchResult?.historyProvince || [];
  const isNotice = searchResult?.isNotice ?? false;

  useEffect(() => {
    dispatch(fetchProvinceTransactionRestriction(id, isShowDataPlain, selectedYearMonth));
    setBreadcrumbs([
      {
        name: 'Pembatasan Transaksi Berdasarkan Provinsi',
        to: '/province-transaction-restriction',
      },
      'Rincian Data Pelanggan',
    ]);
  }, [dispatch, id, isShowDataPlain, selectedYearMonth, setBreadcrumbs]);

  useEffect(() => {
    return () => {
      dispatch(clearHistoryLogRedux('dataRequestFile'));
    };
  }, [dispatch]);

  const handleShowDataPlain = () => {
    setIsShowDataPlain(!isShowDataPlain);
  };

  const handlePreviewDocument = (supportDocument) => {
    setIsPreviewOpen(true);
    dispatch(clearHistoryLogRedux('dataRequestFile'));
    dispatch(fetchRequestFile({ fileName: supportDocument }));
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    dispatch(clearHistoryLogRedux('dataRequestFile'));
  };

  return (
    <div>
      <Card px={24} radius="12px" withBorder>
        <Card.Section className={styles.topSection} p="md">
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={10}
            justify="space-between"
            w="100%"
          >
            <Flex align="center" gap={10}>
              <Text size="lg" weight={600}>
                Data Pelanggan LPG 3 Kg
              </Text>
            </Flex>
            <Button
              className={styles.btnIcon}
              disabled={dataProvinceTransactionRestriction.isLoading}
              onClick={handleShowDataPlain}
              style="blue"
            >
              {dataProvinceTransactionRestriction.isLoading && <LoadingBars size={1} />}
              <img
                alt={isShowDataPlain ? 'show' : 'hide'}
                height={10}
                src={`/assets/ic-password-${isShowDataPlain ? 'show' : 'hide'}-white.svg`}
                width={15}
              />
              {isShowDataPlain ? 'Tampilkan Data' : 'Sembunyikan Data'}
            </Button>
          </Flex>
        </Card.Section>
        {dataProvinceTransactionRestriction.isLoading ? (
          <Card className={styles.loadingCard} p="md" radius="12px" withBorder>
            <Skeleton />
          </Card>
        ) : (
          <div className={styles.contentSection}>
            <div className={styles.dataList}>
              <DetailDataRow label="Nama Pelanggan" value={searchResult.name || '-'} />
              <DetailDataRow
                label="No. KTP Pemilik"
                value={searchResult.nationalityId || '-'}
              />
              <DetailDataRow
                label="No. Handphone Pelanggan"
                value={searchResult.phoneNumber || '-'}
              />
              <DetailDataRow label="Email Pelanggan" value={searchResult.email || '-'} />
              <DetailDataRow
                label="Jenis Pelanggan"
                value={
                  searchResult?.customerTypes?.length ? (
                    <DetailCustomerTypes customerTypes={searchResult.customerTypes} />
                  ) : (
                    '-'
                  )
                }
              />
              <DetailDataRow
                label="Provinsi Transaksi"
                value={
                  activeProvinces.length > 0 ? (
                    <DetailProvinceList provinces={activeProvinces} />
                  ) : (
                    '-'
                  )
                }
              />
            </div>
            <Card className={styles.historyCard} radius="12px" withBorder>
              <Card.Section className={styles.historyHeader} p="md">
                <Flex
                  align={{ base: 'flex-start', md: 'center' }}
                  direction={{ base: 'column', md: 'row' }}
                  gap="md"
                  justify="space-between"
                  w="100%"
                >
                  <Flex align="center" gap={10}>
                    <IconHistoryFilled className={styles.iconHistoryFilled} />
                    <Text className={styles.historyHeading} style={{ fontWeight: 600 }}>
                      Riwayat Hapus/Set Provinsi Transaksi Pelanggan
                    </Text>
                  </Flex>
                  <HistoryMonthYearFilter
                    month={selectedMonth}
                    onMonthChange={setSelectedMonth}
                    onYearChange={setSelectedYear}
                    year={selectedYear}
                  />
                </Flex>
              </Card.Section>
              <div className={styles.historyBody}>
                {isNotice && (
                  <HistoryPeriodNotice yearMonth={selectedYearMonth} />
                )}
                <HistoryProvinceLog
                  history={historyProvince}
                  onPreviewDocument={handlePreviewDocument}
                />
              </div>
            </Card>
          </div>
        )}
      </Card>

      <DocumentPreviewModal
        fileUrl={dataRequestFile?.url || ''}
        isLoading={isLoadingRequestFile}
        onClose={handleClosePreview}
        opened={isPreviewOpen}
      />
    </div>
  );
}
